import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, FileText, ChevronRight, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateShort } from "@/shared/format";

/**
 * `AccountantReviewCard` — the CPA-requested exception summary block
 * shown on the Tax page and (server-side) baked into every Accountant
 * Pack PDF via `pdf_utils.draw_accountant_review_page`.
 *
 * Design intent:
 *   • Advisory ONLY — never blocks Share/Download. The card sits above
 *     the sticky pack CTA so it's visible without being a gate.
 *   • Reuses backend `/api/tax/accountant-review` which itself reuses the
 *     smart-flags engine — one source of truth for exception counts.
 *   • CPA second-look: every row is expandable, showing the underlying
 *     transactions with an inline "Fix" deep-link to Entry Edit + the
 *     existing Fix-Now amber ring. Also carries a short "action needed"
 *     line so the accountant sees the tradie's next step in-context.
 */

// Per-type metadata — action-needed copy, Entry Edit `?fix=` key, and
// the singular label used in expanded panels. Kept alongside the item
// list so the JSON payload and this table are the only two edit points.
const TYPE_META = {
  evidence: {
    action: "Attach photo/PDF evidence",
    fix: "no_receipt",
  },
  unassigned: {
    action: "Assign a job",
    fix: "unassigned_job",
  },
  gst: {
    action: "Review GST on entry",
    fix: "gst_mismatch",
  },
  subbies: {
    action: "Add subcontractor ABN",
    fix: "missing_abn_subcontractor",
  },
  categorisation: {
    action: "Confirm private vs business",
    // Categorisation-pending isn't a Fix-Now flag — the tradie edits
    // status/deduction category directly on the row, so no `?fix=`.
    fix: null,
  },
};

function formatAmount(n) {
  const v = Number(n || 0);
  return v.toLocaleString("en-AU", {
    style: "currency", currency: "AUD", minimumFractionDigits: 2,
  });
}

export default function AccountantReviewCard({ fy, testId = "accountant-review-card" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/tax/accountant-review", { params: { fy } });
        if (!cancelled) setData(data);
      } catch {
        // Silent — card just doesn't render on error.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fy]);

  if (loading || !data) return null;

  const rows = [
    { key: "evidence",       label: "Missing receipt / evidence",
      count: data.evidence_missing_count,
      hint: `Coverage ${data.evidence_coverage_pct}% (${data.total_entries - data.evidence_missing_count}/${data.total_entries})` },
    { key: "unassigned",     label: "Unassigned — no job attached",
      count: data.unassigned_job_count,
      hint: "Site profitability may be incomplete" },
    { key: "gst",            label: "Uncertain GST treatment",
      count: data.gst_uncertain_count,
      hint: "Locked GST differs from Total ÷ 11" },
    { key: "subbies",        label: "Subcontractors missing ABN",
      count: data.subcontractor_no_abn_count,
      hint: "TPAR requires an ABN for each" },
    { key: "categorisation", label: "Private vs business — pending review",
      count: data.categorisation_pending_count,
      hint: "Tradie hasn't approved yet" },
  ];

  const total = data.total_needing_review;
  const items = data.items || {};

  const openEntry = (entryId, fixKey) => {
    if (!entryId) return;
    const qs = fixKey ? `?fix=${fixKey}` : "";
    nav(`/entries/${entryId}/edit${qs}`);
  };

  return (
    <div data-testid={testId} className="bi-elevate rounded-2xl p-4 sm:p-5 mt-4">
      <div className="flex items-center gap-3 mb-3">
        {total > 0 ? (
          <div className="w-9 h-9 rounded-lg bg-[#FBBF24]/15 text-[#FBBF24] inline-flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4" />
          </div>
        ) : (
          <div className="w-9 h-9 rounded-lg bg-white/[0.04] text-white/40 inline-flex items-center justify-center shrink-0 text-[13px] font-heading font-black">
            0
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-heading font-bold text-white leading-tight">
            Issues to review
          </div>
          <div className="text-[11px] text-white/50 leading-tight mt-0.5">
            {total > 0
              ? `${total} item${total === 1 ? "" : "s"} to double-check before lodgement`
              : "No open flags for this view."}
          </div>
        </div>
      </div>

      <ul className="space-y-1.5">
        {rows.map((r) => {
          const meta = TYPE_META[r.key] || {};
          const bucket = items[r.key] || { list: [], more_count: 0 };
          const isOpen = openKey === r.key;
          const canExpand = r.count > 0 && bucket.list.length > 0;
          return (
            <li
              key={r.key}
              data-testid={`accountant-review-${r.key}`}
              className="rounded-lg bg-white/[0.03] border border-white/[0.06] overflow-hidden"
            >
              <button
                type="button"
                data-testid={`accountant-review-${r.key}-header`}
                onClick={() => canExpand && setOpenKey(isOpen ? null : r.key)}
                aria-expanded={isOpen}
                disabled={!canExpand}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition ${canExpand ? "hover:bg-white/[0.02] cursor-pointer" : "cursor-default"}`}
              >
                <span
                  className={`text-sm font-heading font-black tabular-nums w-8 text-right ${r.count > 0 ? "text-[#FBBF24]" : "text-white/30"}`}
                >
                  {r.count}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white/85 leading-tight truncate">
                    {r.label}
                  </div>
                  <div className="text-[10px] text-white/45 leading-tight mt-0.5 truncate">
                    {r.hint}
                  </div>
                </div>
                {canExpand ? (
                  isOpen
                    ? <ChevronDown className="w-3.5 h-3.5 text-white/40 shrink-0" />
                    : <ChevronRight className="w-3.5 h-3.5 text-white/40 shrink-0" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-white/15 shrink-0" />
                )}
              </button>

              {isOpen && (
                <div
                  data-testid={`accountant-review-${r.key}-panel`}
                  className="border-t border-white/[0.06] bg-black/[0.15] px-3 py-2 space-y-1.5"
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#FF9500] font-heading font-bold">
                    Action needed · <span className="text-white/70 normal-case tracking-normal font-medium">{meta.action}</span>
                  </div>
                  {bucket.list.map((it) => (
                    <button
                      key={it.entry_id}
                      type="button"
                      data-testid={`accountant-review-item-${it.entry_id}`}
                      onClick={() => openEntry(it.entry_id, meta.fix)}
                      className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-white/[0.05] transition group"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] text-white/90 leading-tight truncate">
                          {it.supplier}
                        </div>
                        <div className="text-[10px] text-white/50 leading-tight mt-0.5 tabular-nums truncate">
                          {formatDateShort(it.entry_date)} · {formatAmount(it.amount)}
                          {it.job_name ? ` · ${it.job_name}` : ""}
                        </div>
                      </div>
                      <span className="text-[10px] font-heading font-bold tracking-wider text-[#FF9500] opacity-0 group-hover:opacity-100 transition">
                        FIX
                      </span>
                      <ChevronRight className="w-3.5 h-3.5 text-white/40 shrink-0" />
                    </button>
                  ))}
                  {bucket.more_count > 0 && (
                    <div
                      data-testid={`accountant-review-${r.key}-more`}
                      className="text-[10px] text-white/40 italic px-2 pt-1"
                    >
                      + {bucket.more_count} more in the ledger
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-3 flex items-start gap-2 text-[11px] text-white/50 leading-snug">
        <FileText className="w-3 h-3 mt-0.5 shrink-0 text-white/40" />
        <span>
          Supporting workpaper only — figures need your accountant&apos;s final review before lodgement.
        </span>
      </div>
    </div>
  );
}
